// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Funciones auxiliares y helpers
 */

import { getBasePath } from './constants';

/**
 * Construye la URL completa de una imagen con cache busting
 * @param {string} url - URL relativa, absoluta, o base64 de la imagen
 * @param {boolean} addCacheBust - Si true, agrega timestamp para cache busting (default: true)
 * @returns {string} URL completa o data URL con cache busting
 * 
 * REGLAS ABSOLUTAS (DEFINITIVAS):
 * 1. URLs que empiezan con "/", "http://", "https://", o "blob:" son URLs válidas - NUNCA base64
 * 2. SOLO "data:image/" puede ser base64 (con prefijo explícito)
 * 3. CUALQUIER otra cosa se trata como ruta relativa (NO base64)
 */
export const construirUrlImagen = (url, addCacheBust = true) => {
  if (!url) return '';
  
  // Convertir a string explícitamente
  if (typeof url !== 'string') {
    url = String(url);
  }
  
  // REGLA ABSOLUTA #1: URLs que empiezan con "/", "http://", "https://", o "blob:" son URLs válidas
  // NUNCA deben tratarse como base64, sin excepciones
  if (url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    // Es una URL/path válida - procesar como URL (más abajo)
    // NO entrar en lógica de base64
    // (Log de verificación retirado del hot path: se ejecutaba en cada render de avatar/perfil.)
  } 
  // REGLA ABSOLUTA #2: SOLO data URLs con prefijo "data:image/" pueden ser base64
  // Cualquier otra cosa se trata como ruta relativa (NO base64)
  else if (url.startsWith('data:image/')) {
    // Es una data URL válida (base64 con prefijo), devolverla tal cual
    return url;
  }
  // REGLA ABSOLUTA #3: CUALQUIER otra cosa que NO sea URL válida ni data URL
  // se trata como ruta relativa (NO base64, sin excepciones)
  else {
    // Tratar como ruta relativa sin / - continuar con procesamiento de URL (más abajo)
    // NO intentar detectar base64 sin prefijo - esto causaba el bug
  } 
  
  // Object URLs: no tocar; si se mezclan con basePath (else inferior) se rompe el <img> de preview local.
  if (url.startsWith('blob:')) {
    return url;
  }

  // PROCESAMIENTO DE URL (para URLs válidas o rutas relativas)
  const basePath = getBasePath();

  if (url.includes('/archatv2/') || url.includes('/archat/')) {
    if (!basePath || basePath === '/') {
      url = url.replace(/\/archatv2\//g, '/').replace(/\/archat\//g, '/');
    } else {
      const legacyPrefix = `${basePath.replace(/\/$/, '')}/`;
      url = url.replace(/\/archatv2\//g, legacyPrefix).replace(/\/archat\//g, legacyPrefix);
    }
  }
  
  // Si ya es una URL completa (http/https): cache busting solo si no tiene ?v= numérico (avatar_version del backend)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (addCacheBust && !/\?v=\d+($|&)|&v=\d+($|&)/.test(url)) {
      const hasQuery = url.indexOf('?') >= 0;
      const separator = hasQuery ? '&' : '?';
      return `${url}${separator}v=${Date.now()}`;
    }
    return url;
  }
  
  // Rutas públicas de avatar servidas por nginx fuera de /app (no anteponer basePath).
  const isRootAvatarPath =
    url.startsWith('/avatars/') ||
    url.startsWith('/storage/avatars/');
  if (isRootAvatarPath) {
    const rootAvatarUrl = url.startsWith('/storage/avatars/')
      ? url.replace('/storage/avatars/', '/avatars/')
      : url;
    return rootAvatarUrl;
  }

  // Rutas de assets ya bajo el prefijo SPA (p. ej. /app/user-images/); no anteponer basePath o se duplica.
  const spaPrefix = getBasePath().replace(/\/$/, '');
  if (spaPrefix && spaPrefix !== '/' && url.startsWith(`${spaPrefix}/`)) {
    let out = url;
    if (addCacheBust && !/\?v=\d+($|&)|&v=\d+($|&)/.test(out)) {
      const hasQuery = out.indexOf('?') >= 0;
      const separator = hasQuery ? '&' : '?';
      out = `${out}${separator}v=${Date.now()}`;
    }
    return out;
  }

  // Construir URL absoluta desde ruta relativa (preserva query string ?v=)
  let finalUrl = url;
  
  // Si la URL empieza con / y hay basePath distinto de /, anteponer basePath (rutas relativas al host)
  const hasBasePathPrefix = !!(
    basePath &&
    basePath !== '/' &&
    (url === basePath || url.startsWith(`${basePath}/`))
  );
  if (url.startsWith('/') && basePath && basePath !== '/' && !hasBasePathPrefix) {
    finalUrl = basePath + url;
  } else if (url.startsWith(basePath)) {
    // Si la URL ya contiene el basePath, no duplicarlo
    finalUrl = url;
  } else if (url.startsWith('/')) {
    if (basePath === '/') {
      finalUrl = url;
    } else if (url.startsWith('/images/') && basePath !== '/') {
      finalUrl = basePath + url;
    } else {
      finalUrl = url;
    }
  } else {
    // Si no empieza con /, agregar basePath
    if (basePath === '/') {
      finalUrl = '/' + url;
    } else {
      finalUrl = basePath + '/' + url;
    }
  }
  
  // Cache busting: no tocar ?v= numérico (avatar_version viene del backend; jamás reemplazar con Date.now())
  if (addCacheBust && !/\?v=\d+($|&)|&v=\d+($|&)/.test(finalUrl)) {
    const hasQuery = finalUrl.indexOf('?') >= 0;
    const separator = hasQuery ? '&' : '?';
    finalUrl = `${finalUrl}${separator}v=${Date.now()}`;
  }

  return finalUrl;
};

/**
 * Genera un fingerprint del navegador para seguridad
 * @returns {Promise<Object>} Datos del fingerprint
 */
export const generarFingerprintNavegador = async () => {
  const fingerprint = {
    userAgent: navigator.userAgent || '',
    language: navigator.language || navigator.userLanguage || '',
    platform: navigator.platform || '',
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: navigator.deviceMemory || 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    resolution: `${screen.width}x${screen.height}` || '',
    canvas: '',
    webgl: ''
  };
  
  // Fingerprint de Canvas
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('ArChat®', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('ArChat®', 4, 17);
      fingerprint.canvas = canvas.toDataURL();
    }
  } catch (e) {
    fingerprint.canvas = 'error';
  }
  
  // Fingerprint de WebGL
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        fingerprint.webgl = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
      }
    }
  } catch (e) {
    fingerprint.webgl = 'error';
  }
  
  return fingerprint;
};

/**
 * Obtiene la zona horaria según el código de país
 * @param {string} codigoPais - Código ISO del país
 * @returns {string} Zona horaria
 */
export const obtenerZonaHorariaPorPais = (codigoPais) => {
  // Mapeo básico de países a zonas horarias
  // En producción, esto debería venir de la base de datos
  const timezones = {
    'AR': 'America/Argentina/Buenos_Aires',
    'US': 'America/New_York',
    'BR': 'America/Sao_Paulo',
    'ES': 'Europe/Madrid',
    'FR': 'Europe/Paris',
    'DE': 'Europe/Berlin',
    'IT': 'Europe/Rome',
    'RU': 'Europe/Moscow',
    'CN': 'Asia/Shanghai',
    'JP': 'Asia/Tokyo',
    'KR': 'Asia/Seoul',
    'SA': 'Asia/Riyadh',
    'IN': 'Asia/Kolkata'
  };
  
  return timezones[codigoPais] || 'UTC';
};

/**
 * Formatea una hora en formato 24 horas con timezone
 * @param {string|Date} date - Fecha a formatear
 * @param {string} timezone - Zona horaria (ej: 'America/Argentina/Buenos_Aires')
 * @returns {string} Hora formateada en formato HH:MM
 */
export const formatearHora24 = (date, timezone = null) => {
  try {
    if (!date) return '';
    
    // CRÍTICO: Asegurar que la fecha se interprete como UTC si viene del servidor
    // Si la fecha viene como string sin 'Z' o '+00:00', agregarlo para forzar UTC
    let dateString = date;
    if (typeof date === 'string') {
      // Si no tiene indicador de timezone, agregar 'Z' para forzar UTC
      // Verificar que no tenga 'Z', '+', o '-' después de la posición 10 (después de la fecha)
      const hasTimezone = date.includes('Z') || 
                          (date.includes('+') && date.indexOf('+') > 10) || 
                          (date.includes('-') && date.lastIndexOf('-') > 10 && date.length > 19);
      
      if (!hasTimezone && date.length >= 19) {
        // Es un string de fecha sin timezone, asumir que viene en UTC del servidor
        // Agregar 'Z' al final para indicar UTC
        dateString = date + 'Z';
      } else {
        dateString = date;
      }
    }
    
    // Crear objeto Date - si tiene 'Z' se interpretará como UTC
    const fecha = new Date(dateString);
    if (isNaN(fecha.getTime())) {
      console.warn('⚠️ [FORMATEAR_HORA] Fecha inválida:', date, 'dateString:', dateString);
      return '';
    }
    
    let hours, minutes;
    
    // Si hay timezone, convertir a esa zona horaria
    if (timezone) {
      try {
        // Método más confiable: usar Intl.DateTimeFormat con formatToParts
        // Esto automáticamente convierte desde UTC (o la hora del objeto Date) al timezone especificado
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          numberingSystem: 'latn' // Asegurar números latinos (0-9)
        });
        
        const parts = formatter.formatToParts(fecha);
        const hourPart = parts.find(p => p.type === 'hour');
        const minutePart = parts.find(p => p.type === 'minute');
        
        if (hourPart && minutePart) {
          // Extraer valores numéricos directamente de las partes
          // CRÍTICO: Limpiar el valor antes de parsear (eliminar espacios, caracteres extra)
          const hourValue = hourPart.value.trim().replace(/[^\d]/g, '');
          const minuteValue = minutePart.value.trim().replace(/[^\d]/g, '');
          
          // CRÍTICO: Asegurar que solo tomamos los primeros 2 dígitos para minutos
          const minuteValueClean = minuteValue.length > 2 ? minuteValue.substring(0, 2) : minuteValue;
          
          hours = parseInt(hourValue, 10);
          minutes = parseInt(minuteValueClean, 10);
          
          // Validar que sean números válidos y estén en rango
          if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            throw new Error('Valores inválidos de formatToParts');
          }
        } else {
          // Fallback: usar el string formateado y parsearlo
          const formatted = formatter.format(fecha);
          const match = formatted.match(/(\d{1,2}):(\d{2})/);
          if (match) {
            hours = parseInt(match[1].trim(), 10);
            minutes = parseInt(match[2].trim(), 10);
          } else {
            throw new Error('No se pudo parsear el formato');
          }
        }
        
        // Asegurar que sean enteros válidos y estén en rango
        hours = Math.floor(Math.max(0, Math.min(23, hours)));
        minutes = Math.floor(Math.max(0, Math.min(59, minutes)));
        
        // Debug: Log para verificar la conversión (solo en desarrollo)
        if (process.env.NODE_ENV === 'development') {
          console.log('🕐 [FORMATEAR_HORA] Conversión:', {
            fechaOriginal: date,
            fechaUTC: fecha.toISOString(),
            timezone: timezone,
            hourPartValue: hourPart?.value,
            minutePartValue: minutePart?.value,
            hours: hours,
            minutes: minutes,
            horaConvertida: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
          });
        }
      } catch (tzError) {
        // Si falla el timezone, usar hora local del navegador
        console.warn('⚠️ [FORMATEAR_HORA] Error con timezone, usando hora local:', tzError, 'timezone:', timezone);
        hours = fecha.getHours();
        minutes = fecha.getMinutes();
      }
    } else {
      // Sin timezone, usar hora local del navegador
      hours = fecha.getHours();
      minutes = fecha.getMinutes();
    }
    
    // Asegurar que hours y minutes sean números válidos
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('⚠️ [FORMATEAR_HORA] Valores inválidos, usando fecha original');
      hours = fecha.getHours();
      minutes = fecha.getMinutes();
    }
    
    // Asegurar rango válido y convertir a enteros (ya deberían ser enteros, pero por seguridad)
    hours = Math.floor(Math.max(0, Math.min(23, Number(hours))));
    minutes = Math.floor(Math.max(0, Math.min(59, Number(minutes))));
    
    // Formatear como string con padding de 2 dígitos
    // CRÍTICO: Asegurar que hours y minutes sean números enteros válidos (0-23, 0-59)
    const h = Math.floor(Math.max(0, Math.min(23, Number(hours))));
    const m = Math.floor(Math.max(0, Math.min(59, Number(minutes))));
    
    // Formatear con padStart - asegurar exactamente 2 dígitos cada uno
    const displayHours = String(h).padStart(2, '0');
    const displayMinutes = String(m).padStart(2, '0');
    
    // Retornar SOLO el formato HH:MM (exactamente 5 caracteres)
    const result = `${displayHours}:${displayMinutes}`;
    
    // Validación final estricta
    if (result.length !== 5 || !/^\d{2}:\d{2}$/.test(result)) {
      console.error('❌ [FORMATEAR_HORA] Formato inválido:', result, 'hours:', h, 'minutes:', m, 'de fecha:', date);
      // Fallback seguro
      return `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.floor(m)).padStart(2, '0')}`;
    }
    
    return result;
  } catch (e) {
    console.error('❌ [FORMATEAR_HORA] Error formateando hora:', e, 'de fecha:', date, 'timezone:', timezone);
    return '';
  }
};

/**
 * Formatea una fecha a formato relativo
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
export const formatearFechaRelativa = (date) => {
  const now = new Date();
  const fecha = new Date(date);
  const diff = now - fecha;
  const minutos = Math.floor(diff / 60000);
  const horas = Math.floor(minutos / 60);
  const dias = Math.floor(horas / 24);
  
  if (minutos < 1) return 'Ahora';
  if (minutos < 60) return `Hace ${minutos} min`;
  if (horas < 24) return `Hace ${horas} h`;
  if (dias < 7) return `Hace ${dias} días`;
  
  return fecha.toLocaleDateString();
};

/**
 * Valida si un archivo es de un tipo permitido
 * @param {File} file - Archivo a validar
 * @param {string[]} allowedTypes - Tipos MIME permitidos
 * @returns {boolean} True si es válido
 */
export const validarTipoArchivo = (file, allowedTypes) => {
  return allowedTypes.includes(file.type);
};

/**
 * Comprime una imagen
 * @param {File} file - Archivo de imagen
 * @param {number} maxWidth - Ancho máximo
 * @param {number} maxHeight - Alto máximo
 * @param {number} quality - Calidad (0-1)
 * @returns {Promise<Blob>} Imagen comprimida
 */
export const comprimirImagen = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, file.type, quality);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
